import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Star, MessageSquare, UserX, CheckCircle, Clock, Users, Building2, Truck, Package, Mail, Plus, X, CheckCircle2, AlertCircle } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { formatRelativeTime } from "@/lib/utils";
import MemberLink from "@/components/MemberLink";
import { useAuth } from "@/hooks/useAuth";
import PartnerInviteLinkCard from "@/components/PartnerInviteLinkCard";

interface PartnerProfile {
  id: string;
  company: string | null;
  companyLogo?: string | null;
  userType: string | null;
  verificationStatus: string;
  adminVerified: boolean;
  upvoteCount: number;
  completedShipments: number;
  dotNumber?: string | null;
  mcNumber?: string | null;
  lastActiveAt?: string | null;
}

interface PendingInvite {
  id: string;
  invitedEmail: string;
  invitedName: string;
  invitedCompany: string | null;
  partnerType: string;
  createdAt: string;
}

interface EmailCheckResult {
  exists: boolean;
  verified?: boolean;
  firstName?: string;
  lastName?: string;
  company?: string;
  userType?: string;
}

function RoleIcon({ userType }: { userType: string | null }) {
  switch (userType) {
    case "Carrier": return <Truck className="w-3 h-3" />;
    case "Shipper": return <Package className="w-3 h-3" />;
    default: return <Building2 className="w-3 h-3" />;
  }
}

export default function PreferredPartners() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const isShipper = user?.userType === "Shipper";

  const { data: partners, isLoading } = useQuery<PartnerProfile[]>({
    queryKey: ["/api/preferred-partners"],
  });

  const removeMutation = useMutation({
    mutationFn: async (partnerId: string) => {
      await apiRequest("DELETE", `/api/preferred-partners/${partnerId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/preferred-partners"] });
      toast({ title: "Partner removed", description: "Removed from your partners list." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to remove partner.", variant: "destructive" });
    },
  });

  const { data: invitesData, refetch: refetchInvites } = useQuery<{ invites: PendingInvite[] }>({
    queryKey: ["/api/partner-invites"],
    enabled: isShipper,
  });
  const pendingInvites = invitesData?.invites ?? [];

  const [inviteForm, setInviteForm] = useState({ name: "", email: "", partnerType: "" });
  const [emailCheckResult, setEmailCheckResult] = useState<EmailCheckResult | null>(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  const cancelInviteMutation = useMutation({
    mutationFn: (inviteId: string) =>
      apiRequest("DELETE", `/api/partner-invites/${inviteId}`),
    onSuccess: () => {
      refetchInvites();
      toast({ title: "Invitation cancelled", description: "The invitation has been cancelled." });
    },
  });

  const handleEmailCheck = async (email: string) => {
    if (!email || !email.includes("@")) {
      setEmailCheckResult(null);
      return;
    }
    setEmailChecking(true);
    try {
      const res = await fetch(`/api/users/check-email?email=${encodeURIComponent(email)}`);
      const data = await res.json();
      setEmailCheckResult(data);
    } catch {
      setEmailCheckResult(null);
    } finally {
      setEmailChecking(false);
    }
  };

  const handleInviteSubmit = async () => {
    setInviteError(null);
    setInviteSuccess(null);
    if (!inviteForm.name.trim() || !inviteForm.email.trim() || !inviteForm.partnerType) {
      setInviteError("Please fill in all fields.");
      return;
    }
    setInviteLoading(true);
    try {
      const res = await apiRequest("POST", "/api/partner-invites", {
        name: inviteForm.name.trim(),
        email: inviteForm.email.trim(),
        partnerType: inviteForm.partnerType,
      });
      const data = await res.json();
      if (data.status === "added") {
        queryClient.invalidateQueries({ queryKey: ["/api/preferred-partners"] });
        setInviteSuccess(`${inviteForm.name} has been added as an active partner.`);
      } else {
        refetchInvites();
        setInviteSuccess(`Invitation sent to ${inviteForm.email}.`);
      }
      setInviteForm({ name: "", email: "", partnerType: "" });
      setEmailCheckResult(null);
    } catch (err: any) {
      setInviteError(err.message ?? "Failed to send invitation. Please try again.");
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold" data-testid="heading-preferred-partners">My Partners</h1>
          {partners && partners.length > 0 && (
            <Badge variant="secondary" data-testid="badge-partner-count">{partners.length}</Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          The brokers, carriers, and shippers you already work with — partners can see and bid on each other's loads directly, without a cold search or waiting for an invite.
        </p>
      </div>

      <PartnerInviteLinkCard />

      {isShipper && (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Plus className="w-5 h-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Add a partner</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Add brokers and carriers you already work with. Active members are added instantly. New members will receive a free invitation to join.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="partner-name">Name</Label>
              <Input
                id="partner-name"
                placeholder="Full name"
                value={inviteForm.name}
                onChange={(e) => setInviteForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="partner-type">Type</Label>
              <Select
                value={inviteForm.partnerType}
                onValueChange={(v) => setInviteForm(f => ({ ...f, partnerType: v }))}
              >
                <SelectTrigger id="partner-type">
                  <SelectValue placeholder="Broker or Carrier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="broker">Broker</SelectItem>
                  <SelectItem value="carrier">Carrier</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3">
            <Label htmlFor="partner-email">Email address</Label>
            <Input
              id="partner-email"
              type="email"
              placeholder="Email address"
              value={inviteForm.email}
              onChange={(e) => {
                setInviteForm(f => ({ ...f, email: e.target.value }));
                setEmailCheckResult(null);
              }}
              onBlur={(e) => handleEmailCheck(e.target.value)}
            />
          </div>

          {emailChecking && (
            <p className="text-sm text-muted-foreground mt-2">Checking...</p>
          )}
          {emailCheckResult && !emailChecking && (
            <div className="mt-2 flex items-start gap-2">
              {emailCheckResult.exists ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-green-700">
                    <strong>{emailCheckResult.firstName} {emailCheckResult.lastName}</strong>
                    {emailCheckResult.company ? ` — ${emailCheckResult.company}` : ""} · Already on Fill The Trucks
                    <br />
                    <span className="text-muted-foreground">They'll be added as an active partner right away.</span>
                  </p>
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-blue-700">
                    <strong>{inviteForm.email}</strong> — Invite to Fill The Trucks
                    <br />
                    <span className="text-muted-foreground">We'll send them a free invite to join Fill The Trucks.</span>
                  </p>
                </>
              )}
            </div>
          )}

          {inviteError && (
            <Alert variant="destructive" className="mt-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{inviteError}</AlertDescription>
            </Alert>
          )}
          {inviteSuccess && (
            <Alert className="mt-3">
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>{inviteSuccess}</AlertDescription>
            </Alert>
          )}

          <Button
            className="mt-4 w-full"
            onClick={handleInviteSubmit}
            disabled={inviteLoading}
            data-testid="button-add-partner"
          >
            {inviteLoading ? "Adding..." : "Add partner"}
          </Button>
        </Card>
      )}

      {isShipper && pendingInvites.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Pending invitations</h2>
            <Badge variant="secondary">{pendingInvites.length}</Badge>
          </div>
          <div className="space-y-3">
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
                    {invite.invitedName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{invite.invitedName}</p>
                    <p className="text-xs text-muted-foreground">
                      {invite.invitedEmail} · {invite.partnerType === "broker" ? "Broker" : "Carrier"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">Invite sent</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => cancelInviteMutation.mutate(invite.id)}
                    data-testid={`button-cancel-invite-${invite.id}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <Card key={i} className="p-4">
              <div className="flex items-center gap-4">
                <Skeleton className="w-10 h-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="w-40 h-4" />
                  <Skeleton className="w-24 h-3" />
                </div>
                <Skeleton className="w-20 h-8" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {!isLoading && (!partners || partners.length === 0) && (
        <Card>
          <CardContent className="py-16 flex flex-col items-center text-center gap-4">
            <Users className="w-12 h-12 text-muted-foreground" />
            <div>
              <p className="font-semibold text-lg mb-1">No partners yet.</p>
              <p className="text-muted-foreground text-sm max-w-sm">
                Add your existing brokers and carriers as partners. They'll be notified when you post a load.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!isLoading && partners && partners.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-lg font-semibold">Active partners</h2>
            <Badge variant="secondary">{partners.length}</Badge>
          </div>
          <div className="space-y-3">
            {partners.map(partner => (
              <Card key={partner.id} className="p-4" data-testid={`card-partner-${partner.id}`}>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <MemberLink
                        userId={partner.id}
                        name={partner.company || "Unknown"}
                        companyLogo={partner.companyLogo}
                        showAvatar
                        avatarSize="md"
                        className="font-medium"
                        data-testid={`link-partner-${partner.id}`}
                      />
                      {partner.userType && (
                        <Badge variant="secondary" className="text-xs flex items-center gap-1">
                          <RoleIcon userType={partner.userType} />
                          {partner.userType}
                        </Badge>
                      )}
                      {partner.adminVerified && (
                        <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                      {formatRelativeTime(partner.lastActiveAt) && (
                        <span className="flex items-center gap-1" data-testid={`text-active-${partner.id}`}>
                          <Clock className="w-3 h-3" />
                          Active {formatRelativeTime(partner.lastActiveAt)}
                        </span>
                      )}
                      {partner.completedShipments > 0 && (
                        <span data-testid={`text-shipments-${partner.id}`}>{partner.completedShipments} shipments</span>
                      )}
                      {partner.upvoteCount > 0 && (
                        <span data-testid={`text-upvotes-${partner.id}`}>+{partner.upvoteCount} upvotes</span>
                      )}
                      {partner.dotNumber && (
                        <span>DOT: {partner.dotNumber}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setLocation(`/shipments?user=${partner.id}`)}
                      data-testid={`button-message-${partner.id}`}
                    >
                      <MessageSquare className="w-3.5 h-3.5 mr-1" />
                      Message
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removeMutation.mutate(partner.id)}
                      disabled={removeMutation.isPending && removeMutation.variables === partner.id}
                      data-testid={`button-remove-partner-${partner.id}`}
                    >
                      <UserX className="w-3.5 h-3.5 mr-1" />
                      {removeMutation.isPending && removeMutation.variables === partner.id ? "Removing..." : "Remove"}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
